import { ReactElement } from 'react'
import { GetServerSidePropsContext } from 'next'
import { TOKEN_VALIDITY_TIMEOUT_MS } from 'shared/constants/timeouts'
import { DomainAssetsInterface } from 'shared/types/domain'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { getRequestHostInfo } from 'shared/utils/request-host'
import { WithCustomDomainResult, withCustomDomain } from 'shared/utils/with-custom-domain'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { getAuthenticationLayout } from 'modules/authentication/layout/get-auhentication-layout'
import { RegisterConfirmForm } from 'modules/authentication/register/components/register-confirm-form'
import { RegisterConfirmFormDashboard } from 'modules/authentication/register/components/register-confirm-form-dashboard'

const RegisterConfirm = ({ hasCustomDomain }: WithCustomDomainResult) => {
  return hasCustomDomain ? <RegisterConfirmForm /> : <RegisterConfirmFormDashboard />
}

const withRegisterConfirmGuard =
  (baseProps: (context: GetServerSidePropsContext) => Promise<any>) =>
  async (context: GetServerSidePropsContext) => {
    const basePropsResult = await baseProps(context)

    if ('redirect' in basePropsResult || 'notFound' in basePropsResult) {
      return basePropsResult
    }

    const { registerHashPath } = context.params ?? {}

    if (typeof registerHashPath !== 'string' || !registerHashPath) {
      return {
        redirect: {
          destination: '/login',
          permanent: false,
        },
      }
    }

    const { host } = getRequestHostInfo(context.req)

    if (!host) {
      return {
        redirect: {
          destination: '/login',
          permanent: false,
        },
      }
    }

    let timeoutId: NodeJS.Timeout | undefined

    try {
      const controller = new AbortController()
      timeoutId = setTimeout(() => controller.abort(), TOKEN_VALIDITY_TIMEOUT_MS)

      const response = await fetch(
        `${process.env.SELF_HOST}/api/security/token-validity?token=${encodeURIComponent(
          registerHashPath,
        )}`,
        {
          method: 'GET',
          signal: controller.signal,
        },
      )
      const validity = response.ok
        ? await response.json()
        : { isValid: false, redirect: null as string | null }

      if (!validity.isValid) {
        return {
          redirect: {
            destination: validity.redirect ?? '/login',
            permanent: false,
          },
        }
      }
    } catch {
      return {
        redirect: {
          destination: '/login',
          permanent: false,
        },
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
    }

    return basePropsResult
  }

export const getServerSideProps = withCustomDomain(
  withRegisterConfirmGuard(withDomainAssets(baseServerSideProps)),
)

RegisterConfirm.getLayout = (
  page: ReactElement,
  props: { domainAssets?: DomainAssetsInterface } & WithCustomDomainResult,
) =>
  getAuthenticationLayout(page, {
    titleKey: 'security.register_confirm.page_title',
    domainAssets: props.domainAssets,
    hasCustomDomain: props.hasCustomDomain,
  })

export default RegisterConfirm
